import { useTranslation } from "react-i18next";
import { useState, useRef } from "react";
import { Row, Col, Dropdown, DropdownDivider } from "react-bootstrap";
import EllipsisVerticalToggle from "../../../shared/Dropdown/EllipsisVerticalToggle";
import { faMicrochip, faMicrophone, faStop, faEye, faComments, faHeart, faRotate, faBullseye, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import crudApi from "@serie3/common/api/crudApi";
import EmojiPicker from "../../../shared/EmojiPicker";
import PostAssistantForm  from "@serie3/mymediaplanner/Modules/Social/Forms/PostAssistantForm";
import { useFormik } from "formik";

const GOALS = [
  { key: "reach",      icon: faEye,      color: "#038edc", label: "Get seen" },
  { key: "engagement", icon: faComments, color: "#20a56e", label: "Get reactions" },
  { key: "brand",      icon: faHeart,    color: "#564ab1", label: "Build trust" },
  { key: "retention",  icon: faRotate,   color: "#f0a500", label: "Keep audience" },
  { key: "conversion", icon: faBullseye, color: "#f1734f", label: "Get customers" },
];

const AIDropdownMenu = ({
  charCount = 0,
  formik,
  editData,
  setShowLoadingModal,
  setAiPreview,
  setAiLoading,
  cursorPositionRef,
  setTextCheckResult,
  setShowTextChecker,
  setCtaHooKResult,
  setShowCtaHookResult,
}) => {
  const { t } = useTranslation();

  const handleAIResult = (response) => {
    setShowCtaHookResult(true);
    setShowLoadingModal(false);
    let options = JSON.parse(response.data);
    setCtaHooKResult(options);
  }

  const aiAssistantForm = PostAssistantForm.createPostAssistantForm(editData.id, handleAIResult, setShowLoadingModal);
  const aiFormik = useFormik(aiAssistantForm);

  const handleAddEmoji = (emoji) => {
    const text = formik.values.text || "";
    const pos = cursorPositionRef.current;
    const newText = text.substring(0, pos) + emoji.native + text.substring(pos);
    formik.setFieldValue("text", newText);
    cursorPositionRef.current = pos + emoji.native.length;
  };

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const [showGoalPicker, setShowGoalPicker] = useState(false);

  const toggleSpeechToText = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = navigator.language || "en-US";
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      const spoken = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join(" ")
        .trim();
      const existing = formik.values.text || "";
      const newText = existing ? existing + "\n\n" + spoken : spoken;
      formik.setFieldValue("text", newText);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognition.start();
    setIsListening(true);
  };

  const finishPost = (response) => {
    setAiPreview(response.data);
  };

  const checkText = (response) => {
    setTextCheckResult(response.data);
    setShowTextChecker(true);
  };

  const aiCall = async (url, postID, callBack, loadingSetter = setShowLoadingModal) => {
    loadingSetter(true);
    const response = await crudApi.justGet(url + postID);
    if (response.ok) {
      callBack(response);
      loadingSetter(false);
    }
  };

  return (
    <>
      <Row>
        <Col className="d-flex align-items-center ">
          <strong>
            {t("Backend.Post.Text")} ({charCount})
          </strong>
        </Col>
        <Col className="text-end">
          <EmojiPicker handleAddEmoji={handleAddEmoji} />
          <button
            type="button"
            title={isListening ? "Stop recording" : "Speech to text"}
            onClick={toggleSpeechToText}
            style={{
              width: 38, height: 38, border: "none", cursor: "pointer",
              borderRadius: 10, padding: 0,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontSize: 16,
              background: isListening ? "rgba(243,78,78,0.1)" : "transparent",
              color: isListening ? "#f34e4e" : "#6b7280",
              transition: "background 0.15s ease, color 0.15s ease",
            }}
            onMouseEnter={e => { if (!isListening) { e.currentTarget.style.background = "rgba(3,142,220,0.08)"; e.currentTarget.style.color = "#038edc"; } }}
            onMouseLeave={e => { if (!isListening) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#6b7280"; } }}
          >
            <FontAwesomeIcon icon={isListening ? faStop : faMicrophone} />
          </button>

          <Dropdown style={{ float: "right" }} onToggle={(open) => { if (!open) setShowGoalPicker(false); }}>
            <EllipsisVerticalToggle icon={faMicrochip} />
            <Dropdown.Menu>
              {!showGoalPicker ? (
                <>
                  <Dropdown.Item
                    onClick={() => {
                      aiCall(
                        SocialEndpoints.posts.ai.finishPost,
                        editData.id,
                        finishPost,
                        setAiLoading
                      );
                    }}
                  >
                    <i className="pli-pen-5 fs-5 me-2" />
                    {t("Backend.Post.FinishPost")}
                  </Dropdown.Item>
                  <DropdownDivider />
                  <Dropdown.Item onClick={(e) => { e.stopPropagation(); setShowGoalPicker(true); }}>
                    <i className="pli-pen-5 fs-5 me-2" />
                    {t("Backend.Post.CreatePostFromBulletpoints")}
                  </Dropdown.Item>
                  <DropdownDivider />
                  <Dropdown.Item
                    onClick={() => {
                      aiCall(
                        SocialEndpoints.posts.ai.checkText,
                        editData.id,
                        checkText
                      );
                    }}
                  >
                    <i className="pli-pen-5 fs-5 me-2" />
                    {t("Backend.Post.CheckText")}
                  </Dropdown.Item>
                  <DropdownDivider />
                  <Dropdown.Item
                    onClick={async () => {
                      await aiFormik.setFieldValue('prompt_type', 'hook');
                      aiFormik.submitForm();
                    }}
                  >
                    <i className="pli-pen-5 fs-5 me-2" />
                    {t("Backend.Post.Hook")}
                  </Dropdown.Item>
                  <DropdownDivider />
                  <Dropdown.Item
                    onClick={async () => {
                      await aiFormik.setFieldValue('prompt_type', 'cta');
                      aiFormik.submitForm();
                    }}
                  >
                    <i className="pli-pen-5 fs-5 me-2" />
                    {t("Backend.Post.Cta")}
                  </Dropdown.Item>
                </>
              ) : (
                <div style={{ padding: "8px 4px", minWidth: 220 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px 10px", cursor: "pointer", color: "#9299a8", fontSize: 12 }}
                    onClick={(e) => { e.stopPropagation(); setShowGoalPicker(false); }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 10 }} />
                    {t("Backend.Post.Assistant.Back")}
                  </div>
                  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "#9299a8", margin: "0 12px 8px" }}>
                    {t("Backend.Post.Assistant.Goal")}
                  </p>
                  {GOALS.map(({ key, icon, color, label }) => (
                    <Dropdown.Item
                      key={key}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setShowGoalPicker(false);
                        setAiLoading(true);
                        const response = await crudApi.createNew(
                          SocialEndpoints.posts.ai.createPostFromBulletpoints + editData.id,
                          JSON.stringify({ prompt_type: "fullpost", goal: key }),
                          null,
                          true
                        );
                        if (response.ok) finishPost(response);
                        setAiLoading(false);
                      }}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px" }}
                    >
                      <span style={{ width: 28, height: 28, borderRadius: 8, background: `${color}18`, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FontAwesomeIcon icon={icon} style={{ color, fontSize: 13 }} />
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
                    </Dropdown.Item>
                  ))}
                </div>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
    </>
  );
};

export default AIDropdownMenu;
